// InlinePredictionCard.js
import React, { useState, useEffect } from 'react';

export const InlinePredictionCard = ({ onPredict, onCancel, isVisible }) => {
    const [prediction, setPrediction] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isVisible) {
            setIsAnimating(true);
        } else {
            const timer = setTimeout(() => {
                setPrediction(null);
                setIsAnimating(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isVisible]);

    if (!isVisible && !isAnimating) return null;

    const handlePredict = () => {
        if (prediction) {
            onPredict(prediction);
        }
    };

    return (
        <div
            className={`inline-prediction-card ${isVisible ? 'show' : 'hide'}`}
            style={{
                transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.95)',
                opacity: isVisible ? 1 : 0,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
        >
            <style>{`
                .inline-prediction-card {
                    background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.15));
                    border: 2px solid rgba(139, 92, 246, 0.4);
                    border-radius: 12px;
                    padding: 12px;
                    backdrop-filter: blur(20px);
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
                    margin-top: 8px;
                }

                .prediction-title {
                    text-align: center;
                    font-size: 0.9rem;
                    font-weight: bold;
                    color: #a78bfa;
                    margin-bottom: 6px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .prediction-subtitle {
                    text-align: center;
                    font-size: 0.7rem;
                    color: #cbd5e1;
                    margin-bottom: 10px;
                }

                .prediction-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 6px;
                    margin-bottom: 10px;
                }

                .prediction-btn {
                    padding: 8px;
                    border-radius: 8px;
                    font-size: 1.2rem;
                    font-weight: bold;
                    border: 2px solid transparent;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    background: rgba(30, 41, 59, 0.6);
                    color: #94a3b8;
                }

                .prediction-btn:hover {
                    background: rgba(51, 65, 85, 0.8);
                    transform: translateY(-2px);
                }

                .prediction-btn.selected {
                    background: linear-gradient(135deg, #8b5cf6, #6366f1);
                    color: white;
                    border-color: rgba(139, 92, 246, 0.6);
                    transform: scale(1.1);
                    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
                }

                .prediction-actions {
                    display: flex;
                    gap: 6px;
                }

                .action-btn {
                    flex: 1;
                    padding: 8px;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 0.75rem;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .action-btn.cancel {
                    background: rgba(239, 68, 68, 0.2);
                    color: #f87171;
                    border: 1px solid rgba(239, 68, 68, 0.3);
                }

                .action-btn.cancel:hover {
                    background: rgba(239, 68, 68, 0.3);
                }

                .action-btn.confirm {
                    background: linear-gradient(135deg, #10b981, #059669);
                    color: white;
                }

                .action-btn.confirm:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
                }

                .action-btn.confirm:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            `}</style>

            <div className="prediction-title">🎯 Tahmin Yap</div>
            <div className="prediction-subtitle">Zar sonucu için 1-6 arası seç</div>
            
            <div className="prediction-grid">
                {[1, 2, 3, 4, 5, 6].map((num) => (
                    <button
                        key={num}
                        onClick={() => setPrediction(num)}
                        className={`prediction-btn ${prediction === num ? 'selected' : ''}`}
                    >
                        {num}
                    </button>
                ))}
            </div>

            <div className="prediction-actions">
                <button onClick={onCancel} className="action-btn cancel">
                    ✕ İptal
                </button>
                <button
                    onClick={handlePredict}
                    disabled={!prediction}
                    className="action-btn confirm"
                >
                    ✓ Onayla
                </button>
            </div>
        </div>
    );
};

// InlineDiceResultCard.js
export const InlineDiceResultCard = ({ diceResult, battleResult, isVisible }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [progress, setProgress] = useState(100);

    useEffect(() => {
        if (isVisible) {
            setIsAnimating(true);
            setProgress(100);
            
            // Progress bar animasyonu
            const duration = 3000;
            const interval = 30;
            const decrement = (100 / duration) * interval;
            
            const timer = setInterval(() => {
                setProgress(prev => {
                    const newProgress = prev - decrement;
                    return newProgress < 0 ? 0 : newProgress;
                });
            }, interval);

            return () => clearInterval(timer);
        } else {
            const timer = setTimeout(() => {
                setIsAnimating(false);
                setProgress(100);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isVisible]);

    if (!isVisible && !isAnimating) return null;

    const isSuccess = diceResult.success;
    const borderColor = isSuccess ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)';
    const bgGradient = isSuccess 
        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))'
        : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))';

    return (
        <div
            className="inline-dice-result-card"
            style={{
                transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.95)',
                opacity: isVisible ? 1 : 0,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: bgGradient,
                borderColor: borderColor,
            }}
        >
            <style>{`
                .inline-dice-result-card {
                    border: 2px solid;
                    border-radius: 12px;
                    padding: 12px;
                    backdrop-filter: blur(20px);
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
                    margin-top: 8px;
                    position: relative;
                    overflow: hidden;
                }

                .result-header {
                    text-align: center;
                    font-size: 1.1rem;
                    font-weight: bold;
                    margin-bottom: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .result-header.success {
                    color: #10b981;
                }

                .result-header.failure {
                    color: #ef4444;
                }

                .dice-row {
                    display: flex;
                    justify-content: space-around;
                    margin-bottom: 10px;
                    gap: 8px;
                }

                .dice-item {
                    text-align: center;
                    flex: 1;
                }

                .dice-value {
                    font-size: 1.5rem;
                    font-weight: bold;
                    margin-bottom: 3px;
                }

                .dice-value.attacker {
                    color: #3b82f6;
                }

                .dice-value.defender {
                    color: #ef4444;
                }

                .dice-label {
                    font-size: 0.65rem;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .score-row {
                    display: flex;
                    justify-content: space-around;
                    padding: 6px;
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 6px;
                    margin-bottom: 6px;
                    gap: 6px;
                }

                .score-item {
                    text-align: center;
                    flex: 1;
                }

                .score-value {
                    font-size: 0.95rem;
                    font-weight: bold;
                    color: white;
                }

                .score-label {
                    font-size: 0.6rem;
                    color: #94a3b8;
                }

                .critical-badge {
                    text-align: center;
                    font-size: 1rem;
                    font-weight: bold;
                    color: #fbbf24;
                    animation: pulse 1s ease-in-out infinite;
                    margin-top: 6px;
                }

                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.8; transform: scale(1.05); }
                }

                .progress-bar {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    height: 2px;
                    background: linear-gradient(90deg, #8b5cf6, #6366f1);
                    transition: width 0.03s linear;
                }
            `}</style>

            <div className={`result-header ${isSuccess ? 'success' : 'failure'}`}>
                {battleResult}
            </div>

            <div className="dice-row">
                <div className="dice-item">
                    <div className="dice-value attacker"> {diceResult.attacker}</div>
                    <div className="dice-label">Saldırgan</div>
                </div>
                <div className="dice-item">
                    <div className="dice-value defender"> {diceResult.defender}</div>
                    <div className="dice-label">Savunucu</div>
                </div>
            </div>

            <div className="score-row">
                <div className="score-item">
                    <div className="score-value">{diceResult.attackerScore.toFixed(1)}</div>
                    <div className="score-label">Saldırı Puanı</div>
                </div>
                <div className="score-item">
                    <div className="score-value">{diceResult.defenderScore.toFixed(1)}</div>
                    <div className="score-label">Savunma Puanı</div>
                </div>
            </div>

            {diceResult.criticalHit && (
                <div className="critical-badge">
                    💥 KRİTİK VURUŞ!
                </div>
            )}

            <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>
    );
};